import type { AxiosResponse } from 'axios';
import Axios from 'axios';

import { useNotificationStore } from '@/common';
import type { BaseResponse } from '@/types';
import { XSRFHeader } from '@/utils/constants/auth';
import { reXSRF } from '@/utils/constants/RegexPatterns';

import routes from '../routes/routes.json';
import { cookiesStorage } from './storage';

export const axios = Axios.create({
  baseURL: '/api',
});

axios.defaults.headers['Content-Type'] = 'application/json';

axios.interceptors.request.use(
  async (config) => {
    const configuration = config;
    const allowedXSRFMethods = ['post', 'put', 'delete', 'patch'];
    const xsrfValue = cookiesStorage.getCookie(XSRFHeader);

    // if (!config.url?.includes(process.env.NEXT_PUBLIC_APP_API_URL_V0 as string))
    //   configuration.headers['om-request-source'] = OMREQUESTSOURCE;
    if (
      xsrfValue &&
      !configuration.url?.includes(
        process.env.NEXT_PUBLIC_APP_API_URL_V0 as string
      )
    ) {
      if (allowedXSRFMethods.find((x) => x === configuration.method)) {
        configuration.headers['X-XSRF-TOKEN'] = xsrfValue;
      }
    }
    return config;
  },

  (error) => {
    const status = error.response?.status;
    let message =
      error.response?.data?.error_description || error.response?.data?.error;

    // refactoring of messages needs to be done
    if (status === 400) {
      message = 'Bad request';
    } else if (status === 401) {
      message = 'Unauthorized';
    } else if (status === 404) {
      message = 'Not found';
    } else if (status === 500) {
      message = 'Internal server error';
    } else if (status === 424) {
      message = 'Details Required';
    }
    useNotificationStore.getState().addNotification({
      type: 'error',
      title: 'Error',
      message,
    });

    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },

  (error) => {
    const status = error.response?.status;
    // refactoring of messages needs to be done
    if (status === 403) {
      const message =
        error.response?.data?.error_description || error.response?.data?.error;
      const isXSRF = message.match(reXSRF);
      if (isXSRF) {
        useNotificationStore.getState().addNotification({
          type: 'error',
          title: 'Try Again',
          message: 'Something went wrong',
        });
      }
    }
    if (status >= 500) {
      const message = error.response?.data?.detail
        ? error.response?.data?.detail
        : 'Internal Server Error';

      useNotificationStore.getState().addNotification({
        type: 'error',
        title: 'Error',
        message,
      });
    } else if (status === 424 || status === 417) {
      return error;
    }
    if (error?.response?.status === 498 || error?.response?.status === 410) {
      window.location.href = routes.auth;
    }

    return Promise.reject(error);
  }
);

export const get = <T>(
  url: string,
  params?: object
): Promise<BaseResponse<T>> => {
  return axios.get(url, { params });
};

export const post = <T>(url: string, data: any): Promise<BaseResponse<T>> => {
  return axios.post(url, data);
};

export const put = <T>(url: string, data: any): Promise<BaseResponse<T>> => {
  return axios.put(url, data);
};

export const remove = <T>(url: string): Promise<BaseResponse<T>> => {
  return axios.delete(url);
};
